import { useSyncExternalStore } from 'react';
import { WorldContentReply, buildCatalogue, type Catalogue } from '@london-life/contracts';
import { api } from './api';

// The game's content (places, what is inside them, what can be done there) comes from the server, published by
// the admin; nothing about it is in this code. The last copy is kept in the browser so the game starts at once,
// then the server is asked for the newest (a publish also tells open games to load it again).

type State = { catalogue: Catalogue | null; status: 'loading' | 'ready' | 'failed' };
const KEY = 'll:content';
let state: State = { catalogue: null, status: 'loading' };
const listeners = new Set<() => void>();
const set = (next: State) => {
  state = next;
  listeners.forEach((listener) => listener());
};

// the copy kept from last time, if it is still readable
try {
  const kept = WorldContentReply.safeParse(JSON.parse(localStorage.getItem(KEY) ?? 'null'));
  if (kept.success) state = { catalogue: buildCatalogue(kept.data.content, kept.data.version), status: 'ready' };
} catch {
  // no storage (private window): load from the server
}

let loading: Promise<void> | null = null;
export function loadContent() {
  loading ??= api
    .worldContent()
    .then((reply) => {
      if (state.catalogue?.version !== reply.version)
        set({ catalogue: buildCatalogue(reply.content, reply.version), status: 'ready' });
      else if (state.status !== 'ready') set({ ...state, status: 'ready' });
      try {
        localStorage.setItem(KEY, JSON.stringify(reply));
      } catch {
        // full or blocked: fine, it is fetched again next time
      }
    })
    .catch(() => {
      if (!state.catalogue) set({ catalogue: null, status: 'failed' });
    })
    .finally(() => {
      loading = null;
    });
  return loading;
}
// a newer version was published: load it if this game has an older one
export function contentPublished(version: number) {
  if (state.catalogue?.version !== version) void loadContent();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
export function useContent() {
  return useSyncExternalStore(subscribe, () => state);
}
// the published content, for screens that only show once it is there
export function useCatalogue() {
  return useContent().catalogue;
}

if (typeof window !== 'undefined') void loadContent();
